import { type EventStreamLine } from '@apps/web/features/phoebe_chat_v3/model/event_types';

export type NdjsonParseProblem = {
    readonly kind:
        | 'invalid_utf8'
        | 'invalid_json'
        | 'invalid_record'
        | 'eof_before_done'
        | 'parser_already_failed';
    readonly lineNumber: number;
    readonly message: string;
};

export type NdjsonParseResult =
    | { readonly ok: true; readonly lines: ReadonlyArray<EventStreamLine> }
    | {
          readonly ok: false;
          readonly lines: ReadonlyArray<EventStreamLine>;
          readonly problem: NdjsonParseProblem;
      };

export type IncrementalNdjsonParser = {
    readonly push: (chunk: Uint8Array) => NdjsonParseResult;
    readonly finish: () => NdjsonParseResult;
};

export type StreamLineClassification =
    | Readonly<{ readonly ok: true; readonly line: EventStreamLine }>
    | Readonly<{ readonly ok: false; readonly message: string }>;

export type StreamLineClassifier = (
    value: Readonly<Record<string, unknown>>,
) => StreamLineClassification;

const STREAMING_DECODE_OPTIONS = { stream: true } as const;

function isJsonObject(
    value: unknown,
): value is Readonly<Record<string, unknown>> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Incrementally frames an LF-delimited byte stream. Protocol validation stays
 * in the injected classifier so every complete JSON object reaches the owning
 * network-boundary schema before the parser accepts it.
 */
export function createNdjsonParser(
    classifyLine: StreamLineClassifier,
): IncrementalNdjsonParser {
    const decoder = new TextDecoder('utf-8', { fatal: true });
    let bufferedLine = '';
    let failedLineNumber: number | null = null;
    let lineNumber = 1;
    let sawDone = false;

    function fail(
        kind: NdjsonParseProblem['kind'],
        message: string,
        lines: ReadonlyArray<EventStreamLine>,
    ): NdjsonParseResult {
        failedLineNumber = lineNumber;
        return {
            ok: false,
            lines,
            problem: { kind, lineNumber, message },
        };
    }

    function parserAlreadyFailed(): NdjsonParseResult {
        const problemLineNumber = failedLineNumber ?? lineNumber;
        return {
            ok: false,
            lines: [],
            problem: {
                kind: 'parser_already_failed',
                lineNumber: problemLineNumber,
                message: 'NDJSON parser has already failed.',
            },
        };
    }

    function processRecord(
        record: string,
        lines: Array<EventStreamLine>,
    ): NdjsonParseResult | null {
        if (record.endsWith('\r')) {
            return fail(
                'invalid_record',
                'NDJSON records must use LF, not CRLF.',
                lines,
            );
        }
        if (record.length === 0) {
            return fail(
                'invalid_record',
                'NDJSON records must not be blank.',
                lines,
            );
        }

        let parsedValue: unknown;
        try {
            parsedValue = JSON.parse(record);
        } catch {
            return fail(
                'invalid_json',
                'NDJSON line is not valid JSON.',
                lines,
            );
        }

        if (!isJsonObject(parsedValue)) {
            return fail(
                'invalid_record',
                'NDJSON record must be a JSON object.',
                lines,
            );
        }

        const classification = classifyLine(parsedValue);
        if (!classification.ok) {
            return fail('invalid_record', classification.message, lines);
        }

        if (sawDone) {
            const message =
                classification.line.type === 'done'
                    ? 'NDJSON stream contained a second done record.'
                    : 'NDJSON stream contained a record after done.';
            return fail('invalid_record', message, lines);
        }

        lines.push(classification.line);
        sawDone = classification.line.type === 'done';
        lineNumber += 1;
        return null;
    }

    function decode(
        bytes: Uint8Array,
        lines: ReadonlyArray<EventStreamLine>,
    ): string | NdjsonParseResult {
        try {
            return decoder.decode(bytes, STREAMING_DECODE_OPTIONS);
        } catch {
            return fail(
                'invalid_utf8',
                'NDJSON contains invalid UTF-8.',
                lines,
            );
        }
    }

    function push(chunk: Uint8Array): NdjsonParseResult {
        if (failedLineNumber !== null) {
            return parserAlreadyFailed();
        }

        const lines: Array<EventStreamLine> = [];
        let segmentStart = 0;

        for (let index = 0; index < chunk.byteLength; index += 1) {
            if (chunk[index] !== 0x0a) {
                continue;
            }

            const decoded = decode(
                chunk.subarray(segmentStart, index + 1),
                lines,
            );
            if (typeof decoded !== 'string') {
                return decoded;
            }

            const record = `${bufferedLine}${decoded.slice(0, -1)}`;
            bufferedLine = '';
            const problem = processRecord(record, lines);
            if (problem !== null) {
                return problem;
            }
            segmentStart = index + 1;
        }

        if (segmentStart < chunk.byteLength) {
            const decoded = decode(chunk.subarray(segmentStart), lines);
            if (typeof decoded !== 'string') {
                return decoded;
            }
            bufferedLine += decoded;
        }

        return { ok: true, lines };
    }

    function finish(): NdjsonParseResult {
        if (failedLineNumber !== null) {
            return parserAlreadyFailed();
        }

        try {
            bufferedLine += decoder.decode();
        } catch {
            return fail('invalid_utf8', 'NDJSON contains invalid UTF-8.', []);
        }

        if (bufferedLine.length > 0) {
            return fail(
                'invalid_record',
                'Final NDJSON record is missing its LF terminator.',
                [],
            );
        }
        if (!sawDone) {
            return fail(
                'eof_before_done',
                'NDJSON stream ended before a done record.',
                [],
            );
        }

        return { ok: true, lines: [] };
    }

    return { push, finish };
}
